import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface TaskyListItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: React.ReactNode;
  iconBg?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  trailing?: React.ReactNode;
  showChevron?: boolean;
}

export const TaskyListItem = React.forwardRef<HTMLDivElement, TaskyListItemProps>(
  (
    {
      className,
      icon,
      iconBg = 'bg-primary/10 text-primary',
      title,
      description,
      trailing,
      showChevron = true,
      onClick,
      ...rest
    },
    ref,
  ) => (
    <div
      ref={ref}
      {...rest}
      onClick={onClick}
      className={cn(
        'p-4 flex items-center justify-between gap-3 transition-colors',
        onClick && 'hover:bg-gray-50 cursor-pointer',
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {icon && (
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center [&_svg]:size-4 shrink-0',
              iconBg,
            )}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-dark truncate">{title}</div>
          {description && (
            <div className="text-xs text-gray-500 truncate">{description}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {trailing}
        {showChevron && <ChevronRight className="w-4 h-4 text-gray-400" />}
      </div>
    </div>
  ),
);
TaskyListItem.displayName = 'TaskyListItem';

export const TaskyList: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div
    {...rest}
    className={cn(
      'bg-white rounded-tasky-md border border-warm-border divide-y divide-warm-border',
      className,
    )}
  >
    {children}
  </div>
);
